/**
 * In lib rather than the app because the same links are built in two places
 * that can't import each other's copy: the notification feed in the browser,
 * and the mention emails the backend sends. `routes.tsx` consumes these
 * segments too — a route table and a path builder that disagree produce links
 * that 404 without either side changing.
 */

export const VERSION_SEGMENT = "versions";
export const CHANNEL_SIZE_SEGMENT = "sizes";
export const SIZE_VARIANT_SEGMENT = "size-variants";

/** Selects a thread once the ad is open. */
export const COMMENT_THREAD_PARAM = "comment";

/**
 * Turns the rail's "show resolved" filter on, for a link to a thread that is
 * already settled.
 *
 * In the link rather than in router state because the same URL is pasted,
 * bookmarked and reloaded, and state survives none of those — a link that only
 * worked on the first click would be the bug this param exists to fix.
 */
export const COMMENT_SHOW_RESOLVED_PARAM = "showResolved";

export type AdConceptPath = {
  projectId: string;
  adBriefId: string;
  assetId: string;
  /** Null for the primary render, which has no channel of its own. */
  channelSizeId: string | null;
};

/**
 * The channel size scopes the thread list, so a link without it would open the
 * ad and find nothing to select.
 *
 * Root-relative because the app's `navigate` injects the workspace slug itself;
 * for a link leaving the process, pass it through {@link absoluteAdStudioUrl}.
 */
export function buildCommentThreadPath({
  projectId,
  adBriefId,
  assetId,
  channelSizeId,
  threadId,
  isThreadResolved = false,
}: AdConceptPath & {
  threadId: string;
  /**
   * Whether the thread is settled as of the link being built. Resolved threads
   * are filtered out of the rail, so the link has to ask for them back.
   */
  isThreadResolved?: boolean;
}): string {
  const detailPath = `/ad-studio/projects/${projectId}/concepts/${adBriefId}/${assetId}`;
  const withSize = channelSizeId
    ? `${detailPath}/${CHANNEL_SIZE_SEGMENT}/${channelSizeId}`
    : detailPath;
  const params = new URLSearchParams({ [COMMENT_THREAD_PARAM]: threadId });

  if (isThreadResolved) {
    params.set(COMMENT_SHOW_RESOLVED_PARAM, "true");
  }

  return `${withSize}?${params.toString()}`;
}

/**
 * Self-serve Ad Studio workspaces are served from their own host and their paths
 * carry no slug, so they pass `workspaceSlug: null`.
 */
export function absoluteAdStudioUrl({
  baseUrl,
  workspaceSlug,
  path,
}: {
  /** App origin, no trailing slash. */
  baseUrl: string;
  /** Omitted for self-serve Ad Studio, whose host is the workspace. */
  workspaceSlug: string | null;
  /** Root-relative, leading slash. */
  path: string;
}): string {
  return workspaceSlug === null
    ? `${baseUrl}${path}`
    : `${baseUrl}/${workspaceSlug}${path}`;
}
